import "./tournament-message-compose.scss";
import { useEffect, useRef, useState } from "react";
import {
  GSButton,
  GSFileSelect,
  GSForm,
  GSSidePanelPage,
  GSTextEditor,
  useFormDataValidation,
} from "golfstatus_react_components";
import {
  faArrowCircleUp,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  updateMessage,
  setCurrentMessage,
  selectCurrentMessage,
  selectMessagesNotifications,
  clearNotifications,
} from "../../../reducers/messagesSlice";
import {
  clearUtilityNotifications,
  selectUtilityNotifications,
} from "../../../reducers/utilitySlice";
import {
  useNotificationBanner,
  useNotificationNavigation,
} from "../../../hooks/notificationHooks";
import {
  useFileSizeValidator,
  useInlineImageUploader,
} from "../../../hooks/formHooks";
import { formatByteSize } from "../../../helpers/Utilities";
import { isEmail } from "../../../helpers/RegexHelper";

const MAX_ATTACHMENT_SIZE = 15000000;
const MAX_INLINE_SIZE = 5000000;

const TournamentMessageCompose = () => {
  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/messages/:messageID/edit");
  const dispatch = useDispatch();
  const currentMessage = useSelector(selectCurrentMessage);
  const messageNotifications = useSelector(selectMessagesNotifications);
  const utilityNotifications = useSelector(selectUtilityNotifications);
  const notifications = [...messageNotifications, ...utilityNotifications];
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const initializedAttachmentsForId = useRef(null);

  const [context, isValid, setIsValid] = useFormDataValidation({
    initialValid: false,
    data: currentMessage,
    setData: (update) => dispatch(setCurrentMessage(update)),
  });

  useEffect(() => {
    dispatch(
      getMessage({
        tournamentID: match?.params?.tournamentID,
        messageID: match?.params?.messageID,
      }),
    );
  }, [dispatch, match?.params?.tournamentID, match?.params?.messageID]);

  useEffect(() => {
    if (currentMessage?.status === "sent") {
      navigate(
        `/${match?.params?.tournamentID}/messages/${match?.params?.messageID}`,
        { replace: true },
      );
    }
  }, [
    currentMessage?.status,
    navigate,
    match?.params?.tournamentID,
    match?.params?.messageID,
  ]);

  useEffect(() => {
    if (
      currentMessage?.id &&
      initializedAttachmentsForId.current !== currentMessage.id &&
      currentMessage?.emailAttachments?.length > 0
    ) {
      initializedAttachmentsForId.current = currentMessage.id;
      setAttachmentFiles(
        currentMessage.emailAttachments.map((a) => ({
          id: a.id,
          url: a.url,
          name: `${a.filename} (${formatByteSize(a.byteSize)})`,
          type: a.contentType,
          size: a.byteSize,
        })),
      );
    }
  }, [currentMessage?.id, currentMessage?.emailAttachments]);

  useNotificationNavigation(
    "saved",
    notifications,
    `/${match?.params?.tournamentID}/messages/${match?.params?.messageID}`,
    "Message",
  );

  const validateAttachmentSize = useFileSizeValidator(MAX_ATTACHMENT_SIZE);
  const uploadInlineImages = useInlineImageUploader(MAX_INLINE_SIZE);

  const handleAttachmentFiles = (files) => {
    const incoming = Array.from(files);
    if (!validateAttachmentSize([...attachmentFiles, ...incoming])) return;
    const newAttachments = incoming.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type,
      name: `${file.name} (${formatByteSize(file.size)})`,
      size: file.size,
    }));
    setAttachmentFiles((prev) => [...prev, ...newAttachments]);
    setIsValid(true);
  };

  const handleRemoveAttachment = (item) => {
    if (item.id) {
      setRemovedAttachmentIds((prev) => [...prev, item.id]);
    }
    setAttachmentFiles((prev) => prev.filter((a) => a !== item));
    setIsValid(true);
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const saveMessage = async () => {
    setIsValid(false);

    const { html: bodyHtml, uploadFailed } = await uploadInlineImages(
      currentMessage?.bodyHtml,
    );
    if (uploadFailed) {
      setIsValid(true);
      return;
    }

    dispatch(clearUtilityNotifications());

    const newFiles = attachmentFiles.filter((a) => a.file);

    const emailAttachments = await Promise.all(
      newFiles.map(async (a) => {
        const dataUrl = await readFileAsDataURL(a.file);
        return { filename: a.file.name, contentType: a.type, dataUrl };
      }),
    );

    dispatch(
      updateMessage({
        tournamentID: match?.params?.tournamentID,
        messageID: match?.params?.messageID,
        data: {
          subject: currentMessage?.subject,
          bodyHtml,
          bodyText: currentMessage?.bodyText,
          replyTo: currentMessage?.replyTo,
          emailAttachments,
          removeEmailAttachmentIds: removedAttachmentIds,
        },
      }),
    );
  };

  const getNavigation = () => {
    return {
      title: "Message Details",
      leftIcon: faChevronLeft,
      leftButtonClick: () => {
        dispatch(clearNotifications());
        navigate(-1);
      },
    };
  };

  const subjectLength = currentMessage?.subject?.length ?? 0;
  const [html, setHtml] = useState(currentMessage?.bodyHtml ?? "");

  useEffect(() => {
    if (currentMessage?.bodyHtml) setHtml(currentMessage?.bodyHtml);
  }, [currentMessage?.id, currentMessage?.bodyHtml]);

  const getFormSections = () => {
    return [
      {
        type: "vertical xx-large-gap",
        fields: [
          {
            label: "SUBJECT",
            required: true,
            isEditable: true,
            ...context.setBinding("subject"),
            placeholder: "Subject",
            hintText: "Tip: Optimal subject lines are around 40 characters",
            charCount: subjectLength,
          },
          ...(currentMessage?.channel === "email"
            ? [
                {
                  label: "MESSAGE",
                  required: true,
                  customView: true,
                  isEditable: true,
                  externalValue: html,
                  failedValidation: context.validationFailed,
                  value: (
                    <GSTextEditor
                      onInternalChange={(html) => {
                        setHtml(html);
                        setIsValid(true);
                      }}
                      defaultState="editor"
                      value={currentMessage?.bodyHtml ?? ""}
                      placeholder="Message"
                      onChange={(html) => context.updateData(html, "bodyHtml")}
                      showVideoEmbed={false}
                    />
                  ),
                },
              ]
            : [
                {
                  label: "MESSAGE",
                  required: true,
                  isEditable: true,
                  type: "text-area",
                  rows: 5,
                  ...context.setBinding("bodyText"),
                  placeholder: "Message",
                },
              ]),
          ...(currentMessage?.channel === "email"
            ? [
                {
                  label: "ATTACHMENTS",
                  customView: true,
                  isEditable: true,
                  value: (
                    <GSFileSelect
                      description="Tap or drag a file to upload. Total combined file size must be under 15 MB."
                      multiple={true}
                      accept=".jpg, .jpeg, .png, .gif, .pdf, .doc, .docx, .xls, .xlsx, .csv"
                      setSelectedFiles={handleAttachmentFiles}
                      removeSourceItem={handleRemoveAttachment}
                      sourceList={attachmentFiles}
                      title={
                        <GSButton buttonIcon={faArrowCircleUp} title="Upload" />
                      }
                    />
                  ),
                },
              ]
            : []),
          ...(currentMessage?.channel === "email"
            ? [
                {
                  label: "REPLY TO",
                  required: true,
                  isEditable: true,
                  ...context.setBinding("replyTo"),
                  placeholder: "Reply To Email",
                  validation: {
                    isValid: isEmail,
                    invalidLabel: "Not a valid email",
                  },
                },
              ]
            : []),
        ],
      },
    ];
  };

  const getContent = () => {
    return (
      <GSForm formTitle="Compose Message" formSections={getFormSections()} />
    );
  };

  const getDrawerActions = () => {
    return [
      {
        name: "Save & Continue",
        isDisabled: !isValid,
        action: saveMessage,
        type: "black",
      },
      {
        name: "Cancel",
        action: () => navigate(-1),
        type: "grey",
      },
    ];
  };

  const getDrawer = () => {
    return { actions: getDrawerActions() };
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
    dispatch(clearUtilityNotifications());
  };

  const [banner] = useNotificationBanner({ notifications, timeoutAction });

  return (
    <tournament-message-compose>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
      />
    </tournament-message-compose>
  );
};

export default TournamentMessageCompose;
